import axiosInstance from '../services/axiosInstance';
import { Company } from './useCompany';

export interface UserData {
  id?: string;
  firstName: string;
  lastName: string;
  phoneNumber?: string;
  dateOfBirth?: string;
  whereDidYouHearAboutUs?: string;
  favoriteSports?: string[];
  nationality?: string;
  email: string;
  isEmailVerified?: boolean;
  isProfileCompleted?: boolean;
  tenantId: string;
  employeeId?: string;
  companyTRN?: string;
  companyName?: string;
  segmentId?: string;
  membershipProviderIds?: string[];
  profileImageUrl?: string;
  /** GCS path; server resolves to idDocumentUrl when returning user */
  idDocumentPath?: string;
  idDocumentUrl?: string;
  idDocumentUploadedAt?: string;
  fcmToken?: string;
  referralCode?: string;
  schoolId?: string;
  schoolName?: string;
}

const useUser = () => {
  const createUser = async (user: UserData, sendVerificationEmail: boolean) => {
    try {
      const { data: response } = await axiosInstance.post(`/users`, {
        ...user,
        sendVerificationEmail,
      });
      return response.data as UserData;
    } catch (error) {
      throw error;
    }
  };

  const getUser = async (id: string) => {
    try {
      const { data: response } = await axiosInstance.get(`/users/${id}`);
      return response.data as UserData;
    } catch (error) {
      throw error;
    }
  };

  const updateUser = async (user: UserData) => {
    try {
      const { data: response } = await axiosInstance.put(`/users/${user.id}`, {
        ...user,
      });
      return response.data as UserData;
    } catch (error) {
      throw error;
    }
  };

  const deleteUser = async (userId: string) => {
    try {
      await axiosInstance.delete(`/users/${userId}`);
    } catch (error) {
      throw error;
    }
  };

  const sendWelcomeEmail = async (id: string) => {
    try {
      await axiosInstance.post(`/users/${id}/welcomeEmail`);
    } catch (error) {
      throw error;
    }
  };

  // Success feedback is owned by the calling screen so users never see
  // duplicate toasts for a single resend action.
  const reSendEmailVerification = async (email: string, company?: Company) => {
    await axiosInstance.post(`/users/auth/resendVerificationEmail`, {
      tenantId: company?.tenantId,
      email,
    });
  };

  const verifyEmailWithCode = async (email: string, code: string, company?: Company) => {
    try {
      const { data: response } = await axiosInstance.post(`/users/auth/verifyEmailWithCode`, {
        email,
        tenantId: company?.tenantId,
        code,
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  const isEmailExists = async (email: string, company?: Company) => {
    try {
      const { data: response } = await axiosInstance.get(`/users/isEmailExists`, {
        params: {
          tenantId: company?.tenantId,
          email: email.toLowerCase(),
        },
      });

      return response.data;
    } catch (error) {
      return false;
    }
  };

  const createReferralCode = async (userId: string) => {
    try {
      const { data: response } = await axiosInstance.post(`/users/${userId}/referralCode`);
      return (response?.data ?? response) as { referralCode: string; user: UserData };
    } catch (error) {
      throw error;
    }
  };

  return {
    createUser,
    getUser,
    updateUser,
    sendWelcomeEmail,
    deleteUser,
    isEmailExists,
    reSendEmailVerification,
    verifyEmailWithCode,
    createReferralCode,
  };
};

export default useUser;
